// ============================================================
// EXCAVATION - digging below the ground plane.
//
// Trenches, craters, sunken roads. Most of it costs nothing at all,
// because the pieces were already right: `y` has been signed since the
// voxel grid was written, elevation has always been a number rather than
// a height above zero, and line of sight has been a THREE-DIMENSIONAL
// segment from the very first commit. A man in a trench with his eyes at
// 0.6 m is a case the engine already handles and never knew it.
//
// Two things do need saying out loud:
//
//   1. EARTH IS IMPLICITLY SOLID below y = 0. Nothing records it, because
//      recording every cubic metre of a planet would be absurd. But a
//      shot fired into a trench has to stop at its wall, so the cells
//      BORDERING a dug region - and only those - are meshed into earth.
//      No shot can ever reach the rest.
//
//   2. The ground plane is drawn as one filled polygon and would paint
//      straight over any hole in it. That is the renderer's problem, and
//      it is solved there (see BattlefieldScene: an SVG path with the
//      boundary as its outer ring and each dug region as a subpath).
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid } from './types';
import { makeSolid } from './solids';
import type { VoxelGrid, Material } from './voxels';
import { cellKey, parseCell, materialAp, derivePassability, greedyMesh, boxShape } from './voxels';

/**
 * What the ground is made of, where nobody has said otherwise.
 *
 * Also in DEFAULT_MATERIALS, so a GM can build with the same earth they
 * dig through - and editing it changes both.
 */
export const EARTH: Material = {
  key: 'earth',
  name: 'Earth',
  colour: '#6b5a45',
  hardness: 6,
  transparency: 0,
};

/**
 * Cells that have been dug out. Key is "x,y,z" with y negative. The value is what the hole is LINED with
 * (plan map-display, F): a material key from the grid's palette, or `true` for earth - which is also what
 * every map from before reads as.
 */
export type Excavation = Record<string, true | string>;

/** The value a cell dug in this material stores: earth (or nothing named) keeps the old `true`. */
const lining = (material?: string): true | string => (material && material !== EARTH.key ? material : true);

export const digKey = cellKey;

/** Has this cell been dug out? */
export function isDug(dug: Excavation | undefined, x: number, y: number, z: number): boolean {
  return Boolean(dug?.[cellKey(x, y, z)]);
}

/** Dig cells, lined with `material` (earth when none). Returns a NEW excavation, or the same one. */
export function dig(dug: Excavation | undefined, keys: string[], material?: string): Excavation {
  const next = { ...(dug || {}) };
  const value = lining(material);
  let changed = false;
  for (const key of keys) {
    // Only below the ground plane is earth; above it there is nothing to
    // remove, and a "dug" cell up there would punch a hole in the sky.
    if (parseCell(key).y >= 0) continue;
    if (next[key] !== value) {
      next[key] = value;
      changed = true;
    }
  }
  return changed ? next : dug || {};
}

/** Fill dug cells back in. Returns a NEW excavation, or the same one. */
export function fillIn(dug: Excavation | undefined, keys: string[]): Excavation {
  if (!dug) return {};
  const present = keys.filter((key) => dug[key]);
  if (!present.length) return dug;
  const next = { ...dug };
  for (const key of present) delete next[key];
  return next;
}

/**
 * How deep the hole is at this column, in CELLS.
 *
 * Counts down from the surface and stops at the first cell that is still
 * earth - an unbroken shaft, because you cannot stand in a cavern with a
 * metre of rock over your head and call it a trench.
 */
export function digDepth(dug: Excavation | undefined, x: number, z: number): number {
  if (!dug) return 0;
  let depth = 0;
  while (dug[cellKey(x, -(depth + 1), z)]) depth++;
  return depth;
}

/** Where the ground actually is at this column, in metres. Zero or below. */
export function groundLevel(grid: VoxelGrid | undefined, x: number, z: number): number {
  if (!grid?.dug) return 0;
  const size = grid.size > 0 ? grid.size : 1;
  return -digDepth(grid.dug, x, z) * size;
}

/**
 * The earth still standing around what has been dug out.
 *
 * Only cells touching a dug cell can ever be crossed by a shot, so those
 * are the only ones worth meshing. Everything deeper is unreachable by
 * construction, and a planet's worth of soil stays where it belongs:
 * nowhere.
 */
export function earthShell(dug: Excavation | undefined): string[] {
  return dug ? [...shellLinings(dug).keys()] : [];
}

/** The keys of a cell's neighbours that are underground - above the ground plane there is air, not earth. */
function earthNeighbours({ x, y, z }: { x: number; y: number; z: number }): string[] {
  return NEIGHBOURS.filter(([, dy]) => y + dy < 0).map(([dx, dy, dz]) => cellKey(x + dx, y + dy, z + dz));
}

const NEIGHBOURS: [number, number, number][] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 0, 1],
  [0, 0, -1],
  [0, -1, 0],
];

const shellCache = new WeakMap<VoxelGrid, Solid[]>();

/**
 * The walls of every trench, as solids.
 *
 * Memoised on grid identity like everything else here, and honouring the
 * same rule: never mutate a grid in place.
 */
export function meshExcavation(grid: VoxelGrid | undefined): Solid[] {
  if (!grid?.dug || !Object.keys(grid.dug).length) return [];
  const cached = shellCache.get(grid);
  if (cached) return cached;

  const size = grid.size > 0 ? grid.size : 1;
  const lined = shellLinings(grid.dug);
  const earth = grid.materials?.earth ?? EARTH;
  const solids = greedyMesh(lined.keys(), (key) => lined.get(key)).map((box) => {
    const material = liningMaterial(grid, box.kind);
    // THE WALL STANDS FOR ALL THE EARTH BEHIND IT - nothing deeper is meshed - so sight and movement through it
    // are always the earth's: a hole lined in glass or smoke must not open the planet. The lining is its look
    // and its hardness (a stone-lined trench is walled in stone).
    return makeSolid({
      ...boxShape(box, size),
      cover: { level: 'none', ap: materialAp(material) },
      coverMode: 'derived',
      transparency: earth.transparency,
      opaque: earth.transparency === 0,
      colour: material.colour,
      terrain: derivePassability(box.h * size, earth.permeability ?? 0),
      label: material.name,
    });
  });

  shellCache.set(grid, solids);
  return solids;
}

/**
 * Each shell cell's lining: the material of the dug cell it borders, so a hole dug in stone is walled and floored
 * in stone. Where it borders two, the one listed later in the excavation wins - the order cells were FIRST dug
 * (re-digging a cell relines it without moving it). Linings mesh apart: greedyMesh joins only a kind with itself.
 */
function shellLinings(dug: Excavation): Map<string, string> {
  const out = new Map<string, string>();
  for (const [key, value] of Object.entries(dug)) {
    const kind = value === true ? EARTH.key : value;
    for (const neighbour of earthNeighbours(parseCell(key))) if (!dug[neighbour]) out.set(neighbour, kind);
  }
  return out;
}

/** The palette's material for a lining; earth for earth, and for a key the palette no longer holds. */
function liningMaterial(grid: VoxelGrid, kind: string): Material {
  return grid.materials?.[kind] ?? grid.materials?.earth ?? EARTH;
}
